import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack,
  Delete,
  DirectionsBus,
  Edit as EditIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import LogActionModal from "./LogActionModal";
import AddScheduleVehicleModal from "../components/AddScheduleVehicleModal";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DailyLog } from "./types";
import { dailyLogApi } from "./apiService";
import { Loading } from "../../../components/Loading";
import RentalExpenses from "./RentalExpenses";
import { formatDate, formatVehicleNumber } from "../../../utils/utils";
import IncomeDetails from "../IncomeDetails";
import {
  commonButtonProps,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import NavigationHeader from "../../../components/NavigationHeader";

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1, px: 0 }}>{children}</Box>}
    </div>
  );
}

const DailyLogs = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLog, setEditingLog] = useState<DailyLog | null>(null);
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicleId");
  const type = searchParams.get("type");
  const [formData, setFormData] = useState<Partial<DailyLog>>({
    tripStartOdometer: 0,
    tripEndOdometer: 0,
    driverName: "",
    tripStartTime: "",
    tripEndTime: "",
    numberOfTrips: 1,
    startPoint: "",
    endPoint: "",
    dateOfEntry: "",
    remarks: null,
    vehicleId: vehicleId || "",
  });
  const { rentalJourneyId } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [incomeSummary, setIncomeSummary] = useState<any>(null);

  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [softDeletedLogs, setSoftDeletedLogs] = useState<DailyLog[]>([]);
  const [showChangeLogs, setShowChangeLogs] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [logToDelete, setLogToDelete] = useState<DailyLog | null>(null);
  const [tabValue, setTabValue] = useState<string>(
    searchParams.get("tab") || "LOGS"
  );
  const [vehicleModalOpen, setVehicleModalOpen] = useState<boolean>(false);
  const isScheduled = searchParams.get("type") === "SCHEDULED";

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // set tab value and update url
    setTabValue(newValue);
    searchParams.set("tab", newValue);
    navigate(`?${searchParams.toString()}`);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setLogToDelete(null);
  };

  const handleDeleteLog = async (log: DailyLog) => {
    if (log) {
      const response = await dailyLogApi.softDeleteDailyLog(
        rentalJourneyId!,
        log
      );
      if (response) {
        getDailyLogs();
        setDeleteModalOpen(false);
      }
    }
  };

  const handleOpenDialog = (log?: DailyLog) => {
    if (log) {
      setEditingLog({ ...log, vehicleId: vehicleId || "" });
      setFormData({ ...log, vehicleId: vehicleId || "" });
    } else {
      setEditingLog(null);
      setFormData({
        tripStartOdometer: 0,
        tripEndOdometer: 0,
        driverName: "",
        tripStartTime: "",
        tripEndTime: "",
        numberOfTrips: 1,
        startPoint: "",
        endPoint: "",
        remarks: null,
        vehicleId: vehicleId || "",
      });
    }
    setOpenDialog(true);
  };

  const handleAddDetails = () => {
    setVehicleModalOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLog(null);
    setFormData({
      tripStartOdometer: 0,
      tripEndOdometer: 0,
      driverName: "",
      tripStartTime: "",
      tripEndTime: "",
      numberOfTrips: 1,
      startPoint: "",
      endPoint: "",
      dateOfEntry: "",
      remarks: null,
      vehicleId: vehicleId || "",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getDailyLogs = async () => {
    setIsLoading(true);
    let promises: any[] = [];
    promises.push(dailyLogApi.getDailyLogs(rentalJourneyId!));
    promises.push(dailyLogApi.getSoftDeletedDailyLogs(rentalJourneyId!));
    const responses = await Promise.all(promises);
    // normal logs
    setBookingDetails(responses[0]?.bookingDetails);
    setLogs(responses[0]?.dailyLogs || []);
    // deleted/changed logs
    setSoftDeletedLogs(responses[1] || []);
    setIsLoading(false);
  };

  useEffect(() => {
    getDailyLogs();
  }, [rentalJourneyId, vehicleId, type]);

  if (isLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  const LogsTable = ({
    logs,
    isDeleted,
  }: {
    logs: DailyLog[];
    isDeleted: boolean;
  }) => {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Sr No.
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Trip Odometer
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Driver Name
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Trip Time
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                No. of Trips
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                From
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                To
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Date of Entry
              </TableCell>
              <TableCell
                sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
              >
                Remarks
              </TableCell>
              {!isDeleted && (
                <TableCell
                  sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
                  align="center"
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, index) => (
                <TableRow key={log.id} hover>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {log.tripStartOdometer} km (Start)
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {log.tripEndOdometer} km (End)
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.driverName}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {formatTime(log.tripStartTime)} (Start)
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {formatTime(log.tripEndTime)} (End)
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.numberOfTrips || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.startPoint || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.endPoint || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {formatDate(log.dateOfEntry)}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.remarks || "-"}
                  </TableCell>
                  {!isDeleted && (
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialog(log)}
                        title="Edit Log"
                        sx={{
                          py: 0.25,
                          px: 0.5,
                          minWidth: "auto",
                          fontSize: "0.75rem",
                          mr: 1,
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setLogToDelete(log);
                          setDeleteModalOpen(true);
                        }}
                        title="Delete Log"
                        sx={{
                          py: 0.25,
                          px: 0.5,
                          minWidth: "auto",
                          fontSize: "0.75rem",
                          borderColor: "error.main",
                          color: "error.main",
                          "&:hover": {
                            borderColor: "error.main",
                            color: "error.main",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleSuccess = (
    vehicleId: string,
    vehicleNumber: string,
    vehicleType: string
  ) => {
    navigate(
      `/rental/${rentalJourneyId}/logs?type=${vehicleType}&vehicleId=${vehicleId}&vehicleNumber=${vehicleNumber}`
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      <Box>
        <NavigationHeader
          title="Daily Logs"
          breadcrumbs={[
            {
              label: "Bookings",
              path: "/rental",
            },
            {
              label: "Daily Logs",
            },
          ]}
          backNavigationTo="/rental"
        />

        {/* Rental Details Section */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mb: 1, fontSize: "1rem" }}
          >
            Booking Summary
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Party Name
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {bookingDetails?.partyName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Vehicle Number
              </Typography>
              {isScheduled ? (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{
                    ...bookingDetailsCellStyles,
                    fontSize: "0.65rem",
                    py: 0.25,
                    px: 1,
                    minWidth: "auto",
                  }}
                  onClick={() => setVehicleModalOpen(true)}
                >
                  Assign Vehicle
                </Button>
              ) : (
                <Typography variant="body1" sx={bookingDetailsCellStyles}>
                  {formatVehicleNumber(bookingDetails?.vehicleNumber)}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Route
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {bookingDetails?.journeyStartLocation} to{" "}
                {bookingDetails?.journeyEndLocation}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Journey Dates
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {formatDate(bookingDetails?.journeyStartDateTime)}
                {bookingDetails?.journeyEndDateTime &&
                  ` to ${formatDate(bookingDetails?.journeyEndDateTime)}`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Income Type
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textTransform: "capitalize",
                  ...bookingDetailsCellStyles,
                }}
              >
                {bookingDetails?.incomeType || "-"}
              </Typography>
            </Grid>
            {bookingDetails?.incomeType === "fixed" && (
              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={bookingDetailsHeaderStyles}
                >
                  Fixed Amount
                </Typography>
                <Typography variant="body1" sx={bookingDetailsCellStyles}>
                  ₹{bookingDetails?.amount}
                </Typography>
              </Grid>
            )}
            {bookingDetails?.incomeType === "perKm" && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Rate
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    ₹{bookingDetails?.amount}/km
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Over Limit Rate
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    ₹{bookingDetails?.overLimitAmount}/km
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Over Limit Kms
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    {bookingDetails?.overLimitKms} km
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Logs" value="LOGS" />
          <Tab label="Expenses" value="EXPENSE" />
          <Tab label="Income" value="INCOME" />
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index="LOGS">
        {isScheduled ? (
          <>
            <AddVehicleAndOwner
              handleAddDetails={handleAddDetails}
              from="daily-logs"
            />
          </>
        ) : (
          <>
            <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ height: "30px", fontSize: "0.75rem" }}
              >
                Add Log
              </Button>
            </Grid>
            <LogsTable logs={logs} isDeleted={false} />

            {softDeletedLogs.length > 0 && (
              <Grid container sx={{ my: 2 }}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "gray",
                      fontSize: "0.75rem",
                      mb: 2,
                    }}
                    onClick={() => {
                      setShowChangeLogs(!showChangeLogs);
                    }}
                  >
                    Changed Logs
                    {showChangeLogs ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </Typography>
                  {showChangeLogs && (
                    <LogsTable logs={softDeletedLogs} isDeleted={true} />
                  )}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index="EXPENSE">
        <RentalExpenses />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index="INCOME">
        <IncomeDetails
          incomeSummary={incomeSummary}
          setIncomeSummary={setIncomeSummary}
          rentalJourneyId={rentalJourneyId!}
        />
      </CustomTabPanel>

      <LogActionModal
        open={openDialog}
        onClose={handleCloseDialog}
        editingLog={editingLog}
        formData={formData}
        rentalJourneyId={rentalJourneyId!}
        getDailyLogs={getDailyLogs}
        rentalJourney={bookingDetails}
      />

      <AddScheduleVehicleModal
        open={vehicleModalOpen}
        onClose={() => setVehicleModalOpen(false)}
        rentalJourneyId={rentalJourneyId!}
        onSuccess={(vehicleId, vehicleNumber, vehicleType) =>
          handleSuccess(vehicleId, vehicleNumber, vehicleType)
        }
      />

      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: 600 }}>
          Delete Log
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to delete this log?
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={closeDeleteModal}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteLog(logToDelete!)}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default DailyLogs;

const deletedTableStyles = {
  fontSize: "0.55rem",
  border: "1px solid #e0e0e0",
};

const bookingDetailsHeaderStyles = {
  fontSize: "0.65rem",
};

const bookingDetailsCellStyles = {
  fontSize: "0.65rem",
};

export const AddVehicleAndOwner = ({
  handleAddDetails,
  from,
}: {
  handleAddDetails: () => void;
  from: string;
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 6,
        textAlign: "center",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <DirectionsBus sx={{ fontSize: 40, color: "text.disabled", mb: 2 }} />
      <Typography
        variant="h5"
        sx={{
          color: "text.secondary",
          mb: 2,
          fontWeight: 500,
        }}
      >
        {from === "daily-logs"
          ? "Assign vehicle to start adding log"
          : "Assign vehicle to start adding expenses"}
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddDetails}
        {...commonButtonProps}
      >
        Assign Vehicle
      </Button>
    </Paper>
  );
};
